import { Button } from "@ui/Button";
import { Tab, TabLabel, Tabs } from "@ui/molecules/Tabs/Tabs";
import {
	createEffect,
	createSignal,
	For,
	type JSX,
	onCleanup,
	untrack,
} from "solid-js";
import type { AccountCompletionItem } from "./accountCompletion";
import { handleTabTrap, OUTSIDE_FOCUS_TRAP } from "./focusTrapTab";
import type { QuestionBoxVideoMentionData } from "./QuestionBoxVideoMention";
import type { QuestionContextAnswer } from "./QuestionContext";
import { QuestionContextAnswers } from "./QuestionContextAnswers";
import {
	type QuestionContextChannel,
	QuestionContextChannels,
} from "./QuestionContextChannels";
import {
	type QuestionContextVideo,
	QuestionContextVideos,
} from "./QuestionContextVideos";
import {
	CONTEXT_LIST_ROW_SELECTOR,
	focusHighlightedContextRow,
	handleContextNavigatorKey,
	highlightIndexForTab,
	type QuestionContextList,
	type QuestionContextNavigator,
} from "./questionContextKeyboard";
import type { QuestionContextVideoSearch } from "./questionContextVideoSearch";
import type { DocMentionData } from "./questionDocument";
import "./QuestionContextSelector.css";

const SELECTOR_HEIGHT_REM = 18;
const OVERLAP_REM = 0.25;
const CONTAINER_SELECTOR = ".gvtui-question-box, .tweak-context-picker";
const FOCUS_TRAP_SCOPE = ".gvtui-question-context-focus-scope";
const POPOVER_ID = "question-context-selector-popover";

type QuestionContextSelectorPlacement = "above" | "below";

export type QuestionContextSelectorTab =
	| "answers"
	| "channels"
	| "files"
	| "groups"
	| "videos";

type QuestionContextSelectorSharedProps = {
	/** @default "standard" */
	theme?: "standard" | "dark";
	/** Tabs to show, in the order given. */
	tabs: QuestionContextSelectorTab[];
	/** @default "Channels" */
	channelsTabLabel?: "Channels" | "Sources" | "Creators";
	videos: QuestionContextVideo[];
	/**
	 * Whether the box browses its pool for what a bare `@` could become, rather
	 * than waiting for a handle to search for (see QuestionBoxProps.contextSource).
	 */
	browsesPool: boolean;
	searchVideos: QuestionContextVideoSearch;
	insertedVideoMentionKeys: () => string[];
	insertVideoMentions: (mentions: QuestionBoxVideoMentionData[]) => void;
	channels: QuestionContextChannel[];
	channelCompletions: AccountCompletionItem[];
	onSelectChannelCompletion: (account: AccountCompletionItem) => void;
	insertedAccountMentionKeys: () => string[];
	insertAccountMentions: (mentions: DocMentionData[]) => void;
	contextAnswerIds: () => string[];
	onAddContextAnswer: (answer: QuestionContextAnswer) => void;
	/** Question the conversation is, whose own answers the Answers tab offers first. */
	contextQuestionId?: string;
	disabled?: boolean;
	/** When set, that tab will not add more items (playbook context max). */
	canAddOnTab?: (tab: QuestionContextSelectorTab) => boolean;
};

type QuestionContextSelectorMentionProps =
	QuestionContextSelectorSharedProps & {
		/** @default "mention" */
		interactionMode?: "mention";
		/**
		 * Tab something outside the selector wants shown — typing `@` in the question
		 * asks for Channels — or `null` to close the selector again. The button opens
		 * and closes it independently of this.
		 */
		requestedTab: () => QuestionContextSelectorTab | null;
		/**
		 * Row of the tab on screen the question box's arrow keys have reached, or -1
		 * for none.
		 */
		highlightedIndex: number;
		/**
		 * The row the question box should highlight, as the tab on screen changes.
		 */
		onHighlightRow: (index: number) => void;
		/** Hands the question box what it needs to drive the selector it opened. */
		registerNavigator: (navigator: QuestionContextNavigator | null) => void;
		/** Starts an `@`-mention in the question (caret + keyboard handling). */
		beginMention: () => void;
		/** Abandons that mention when the selector closes with nothing selected. */
		onDismiss: () => void;
		/**
		 * The `@handle` being written in the question. Whichever tab is on screen
		 * searches for it.
		 */
		mentionQuery: string;
	};

/** Standalone “+” popover (e.g. playbook tweak context) — no question input. */
type QuestionContextSelectorPickerProps = QuestionContextSelectorSharedProps & {
	interactionMode: "picker";
	mentionQuery?: string;
	/** Fired when the popover opens or closes (e.g. to load source completions). */
	onPickerOpenChange?: (open: boolean) => void;
};

export type QuestionContextSelectorProps =
	| QuestionContextSelectorMentionProps
	| QuestionContextSelectorPickerProps;

type Props = QuestionContextSelectorProps;

const isMentionProps = (
	props: Props,
): props is QuestionContextSelectorMentionProps =>
	props.interactionMode !== "picker";

export const QuestionContextSelector = (props: Props) => {
	const isPicker = () => props.interactionMode === "picker";
	const [isOpen, setIsOpen] = createSignal(false);
	const [pickerHighlightedIndex, setPickerHighlightedIndex] = createSignal(-1);

	const setOpen = (open: boolean) => {
		setIsOpen(open);
		if (isPicker() && "onPickerOpenChange" in props) {
			props.onPickerOpenChange?.(open);
		}
	};

	const highlightedIndex = () => {
		if (isPicker()) {
			return pickerHighlightedIndex();
		}
		return isMentionProps(props) ? props.highlightedIndex : -1;
	};

	const setHighlightedIndex = (index: number) => {
		if (isPicker()) {
			setPickerHighlightedIndex(index);
			return;
		}
		if (isMentionProps(props)) {
			props.onHighlightRow(index);
		}
	};

	const mentionQuery = () => {
		if (isPicker()) {
			return props.mentionQuery ?? "";
		}
		return isMentionProps(props) ? props.mentionQuery : "";
	};
	const [selectedTab, setSelectedTab] =
		createSignal<QuestionContextSelectorTab>();
	const [placement, setPlacement] =
		createSignal<QuestionContextSelectorPlacement>("above");
	const [rightOffset, setRightOffset] = createSignal(0);
	let buttonEl: HTMLButtonElement | undefined;

	/** Rows each tab is showing, published by the tabs themselves. */
	const lists = new Map<QuestionContextSelectorTab, QuestionContextList>();

	const registerList =
		(tab: QuestionContextSelectorTab) => (list: QuestionContextList) => {
			lists.set(tab, list);
			onCleanup(() => {
				if (lists.get(tab) === list) lists.delete(tab);
			});
		};

	/** The tab on screen: `Tabs` shows the first one until we choose another. */
	const activeTab = () => selectedTab() ?? props.tabs[0];

	/** The row each tab was left on, so that arrowing back returns to it. */
	const highlightByTab = new Map<QuestionContextSelectorTab, number>();

	const canAddOnTab = (tab: QuestionContextSelectorTab) =>
		props.canAddOnTab?.(tab) ?? true;

	const firstAddableTab = () =>
		props.tabs.find((tab) => canAddOnTab(tab)) ?? props.tabs[0];

	const tabOnOpen = (fallback: QuestionContextSelectorTab) => {
		const remembered = selectedTab();
		if (
			remembered &&
			props.tabs.includes(remembered) &&
			canAddOnTab(remembered)
		) {
			return remembered;
		}
		if (props.tabs.includes(fallback) && canAddOnTab(fallback)) {
			return fallback;
		}
		return firstAddableTab();
	};

	// Untracked throughout: this is a command, called from the question box's key
	// handler and from the effect below, and what it reads to do its job (the
	// highlight, a tab's rows) must not become a dependency of either caller.
	const selectTab = (
		tab: QuestionContextSelectorTab,
		options?: { reopen?: boolean },
	) =>
		untrack(() => {
			const leaving = activeTab();
			if (tab === leaving && !options?.reopen) return;
			if (leaving && tab !== leaving) {
				highlightByTab.set(leaving, highlightedIndex());
			}
			setSelectedTab(tab);
			setHighlightedIndex(
				highlightIndexForTab(
					highlightByTab.get(tab),
					lists.get(tab)?.count() ?? 0,
				),
			);
		});

	/** Closes the selector on nothing having been taken from it. */
	const dismiss = () => {
		highlightByTab.set(activeTab(), highlightedIndex());
		setOpen(false);
		if (isMentionProps(props)) {
			props.onDismiss();
		}
	};

	createEffect(() => {
		if (props.disabled && untrack(isOpen)) {
			dismiss();
		}
	});

	createEffect(() => {
		if (!isMentionProps(props)) {
			return;
		}
		const requested = props.requestedTab();
		if (!requested) {
			setOpen(false);
			return;
		}
		// The requested tab is only taken as the selector opens. This runs again
		// on every keystroke of the handle being typed — which is what puts the
		// selector back on screen if it was dismissed mid-handle — and re-asserting
		// the tab then would drag the user back from one they had moved to. A box
		// without the tab asked for opens on its first.
		if (!untrack(isOpen)) {
			selectTab(tabOnOpen(requested), { reopen: true });
		}
		setOpen(true);
	});

	/** Tab lists fetch only while the popover is open and their tab is selected. */
	const tabIsLive = (tab: QuestionContextSelectorTab) =>
		isOpen() && activeTab() === tab;

	/** The highlight belongs to the tab on screen; the rest show none. */
	const highlightIn = (tab: QuestionContextSelectorTab) =>
		activeTab() === tab ? highlightedIndex() : -1;

	// A list often arrives after its tab is shown. The highlight is already the
	// first row (or the row this tab was left on); an empty list must not clear
	// it, or that row is never marked once the list shows up. Once rows are
	// there, a highlight sitting past the end moves down onto one that is still
	// there. A highlight cleared by typing is left cleared — only a list that
	// was empty puts the first row back.
	let rowsOnShow = 0;
	createEffect(() => {
		const tab = activeTab();
		const rows = lists.get(tab)?.count() ?? 0;
		const index = untrack(highlightedIndex);
		if (rows <= 0) {
			rowsOnShow = 0;
			return;
		}
		const arrived = rowsOnShow === 0;
		rowsOnShow = rows;
		if (arrived && index < 0) {
			setHighlightedIndex(highlightIndexForTab(undefined, rows));
			return;
		}
		if (index >= rows) {
			setHighlightedIndex(highlightIndexForTab(index, rows));
		}
	});

	const contextNavigator: QuestionContextNavigator = {
		count: () => lists.get(activeTab())?.count() ?? 0,
		select: (index) => lists.get(activeTab())?.select(index),
		stepTab: (delta) => {
			const tabs = props.tabs;
			const current = tabs.indexOf(activeTab());
			if (current === -1) return;
			// The strip's ends are walls rather than a loop, so the arrow keys can't
			// take the user somewhere they didn't mean to go.
			const next = tabs[current + delta];
			if (next) selectTab(next);
		},
	};

	// The question box only drives the selector while it is on screen; the moment
	// it closes the arrow keys go back to moving the caret.
	createEffect(() => {
		if (!isMentionProps(props) || !isOpen()) return;
		props.registerNavigator(contextNavigator);
		onCleanup(() => props.registerNavigator(null));
	});

	const tabContent: Record<QuestionContextSelectorTab, () => JSX.Element> = {
		answers: () => (
			<Tab
				class="answers"
				id="answers"
				label={<TabLabel symbol="auto_awesome" label="Answers" />}
			>
				<QuestionContextAnswers
					searchQuery={mentionQuery()}
					active={tabIsLive("answers")}
					contextQuestionId={props.contextQuestionId}
					addedAnswerIds={props.contextAnswerIds}
					onAddAnswer={props.onAddContextAnswer}
					canAddMore={() => canAddOnTab("answers")}
					highlightedIndex={highlightIn("answers")}
					registerList={registerList("answers")}
				/>
			</Tab>
		),
		channels: () => (
			<Tab
				class="channels"
				id="channels"
				label={
					<TabLabel
						symbol="account_circle"
						label={props.channelsTabLabel ?? "Channels"}
					/>
				}
			>
				<QuestionContextChannels
					surface={props.theme === "dark" ? "inverse" : "standard"}
					channels={props.channels}
					completions={props.channelCompletions}
					searchQuery={mentionQuery()}
					browsesPool={props.browsesPool}
					channelsTabLabel={props.channelsTabLabel}
					onSelectCompletion={props.onSelectChannelCompletion}
					insertedAccountMentionKeys={props.insertedAccountMentionKeys}
					insertAccountMentions={props.insertAccountMentions}
					canAddMore={() => canAddOnTab("channels")}
					highlightedIndex={highlightIn("channels")}
					registerList={registerList("channels")}
				/>
			</Tab>
		),
		files: () => (
			<Tab id="files" label={<TabLabel symbol="description" label="Files" />}>
				Files
			</Tab>
		),
		groups: () => (
			<Tab id="groups" label={<TabLabel symbol="group" label="Groups" />}>
				Groups
			</Tab>
		),
		videos: () => (
			<Tab
				class="videos"
				id="videos"
				label={<TabLabel symbol="smart_display" label="Videos" />}
			>
				<QuestionContextVideos
					videos={props.videos}
					searchQuery={mentionQuery()}
					browsesPool={props.browsesPool}
					active={tabIsLive("videos")}
					searchVideos={props.searchVideos}
					insertedVideoMentionKeys={props.insertedVideoMentionKeys}
					insertVideoMentions={props.insertVideoMentions}
					canAddMore={() => canAddOnTab("videos")}
					highlightedIndex={highlightIn("videos")}
					registerList={registerList("videos")}
				/>
			</Tab>
		),
	};

	const updatePlacement = () => {
		const container = buttonEl?.closest(CONTAINER_SELECTOR);
		if (!container || !buttonEl) return;
		const containerRect = container.getBoundingClientRect();
		const buttonRect = buttonEl.getBoundingClientRect();
		const rem = Number.parseFloat(
			getComputedStyle(document.documentElement).fontSize,
		);
		const height = SELECTOR_HEIGHT_REM * rem;
		const overlap = OVERLAP_REM * rem;
		setPlacement(containerRect.top >= height - overlap ? "above" : "below");
		setRightOffset(containerRect.right - buttonRect.right);
	};

	createEffect(() => {
		if (!isOpen()) return;
		updatePlacement();
		const container = buttonEl?.closest(CONTAINER_SELECTOR);
		const observer = new ResizeObserver(updatePlacement);
		if (container) observer.observe(container);
		window.addEventListener("resize", updatePlacement);
		window.addEventListener("scroll", updatePlacement, true);
		const handleMouseDown = (event: MouseEvent) => {
			const target = event.target as HTMLElement | null;
			if (!target) return;
			if (buttonEl?.contains(target)) return;

			if (target.closest(".gvtui-question-context-selector")) return;
			// Repositioning the caret inside `@…` must not abandon the mention.
			if (
				(target.closest(FOCUS_TRAP_SCOPE) &&
					!target.closest(OUTSIDE_FOCUS_TRAP)) ||
				target.closest(".question-container")
			) {
				return;
			}
			dismiss();
		};
		document.addEventListener("mousedown", handleMouseDown);
		onCleanup(() => {
			observer.disconnect();
			window.removeEventListener("resize", updatePlacement);
			window.removeEventListener("scroll", updatePlacement, true);
			document.removeEventListener("mousedown", handleMouseDown);
		});
	});

	createEffect(() => {
		if (!isOpen()) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				event.preventDefault();
				dismiss();
				queueMicrotask(() => {
					if (isMentionProps(props)) {
						const container = buttonEl?.closest(CONTAINER_SELECTOR);
						container?.querySelector<HTMLElement>(".question")?.focus();
					} else {
						buttonEl?.focus();
					}
				});
				return;
			}

			const scope =
				buttonEl?.closest(FOCUS_TRAP_SCOPE) ??
				buttonEl?.closest(CONTAINER_SELECTOR);
			if (scope instanceof HTMLElement) {
				handleTabTrap(scope, event);
			}

			const target = event.target;
			if (!(target instanceof HTMLElement)) return;
			if (!target.closest(CONTEXT_LIST_ROW_SELECTOR)) return;

			const handled = handleContextNavigatorKey(
				contextNavigator,
				highlightedIndex(),
				event,
				setHighlightedIndex,
				{ selectOnEnter: false },
			);
			if (!handled) return;

			event.preventDefault();
			if (
				event.key === "ArrowDown" ||
				event.key === "ArrowUp" ||
				event.key === "ArrowLeft" ||
				event.key === "ArrowRight"
			) {
				queueMicrotask(() =>
					focusHighlightedContextRow(document.getElementById(POPOVER_ID)),
				);
			}
		};

		document.addEventListener("keydown", onKeyDown, true);
		onCleanup(() => document.removeEventListener("keydown", onKeyDown, true));
	});

	return (
		<>
			<Button
				ref={(el) => {
					buttonEl = el;
				}}
				type="button"
				variant="secondary"
				theme="monochrome"
				shape="square"
				size="large"
				symbol="add"
				aria-label="Add"
				disabled={
					props.disabled || !props.tabs.some((tab) => canAddOnTab(tab))
				}
				aria-expanded={isOpen()}
				aria-haspopup="dialog"
				aria-controls={isOpen() ? POPOVER_ID : undefined}
				// The caret belongs to the question throughout, so the button never
				// takes the focus it would need to be taken back from.
				onMouseDown={(event) => event.preventDefault()}
				onClick={() => {
					if (isOpen()) {
						dismiss();
						return;
					}
					if (isPicker()) {
						selectTab(tabOnOpen(props.tabs[0]), { reopen: true });
						setOpen(true);
						return;
					}
					// Opened by hand, the selector is the one a typed `@` would have
					// opened: the mention goes into the question and Channels leads.
					if (!isMentionProps(props)) return;
					props.beginMention();
					selectTab(tabOnOpen("channels"), { reopen: true });
					setOpen(true);
				}}
			/>
			<Tabs
				id={POPOVER_ID}
				theme={props.theme}
				tabIndicator="underline"
				presentation="popover"
				variant="card"
				tabListStyle="flex-grow"
				aria-label="Question context"
				class="gvtui-question-context-selector"
				classList={{
					"gvtui-question-context-selector--dark": props.theme === "dark",
					"gvtui-question-context-selector--above": placement() === "above",
					"gvtui-question-context-selector--below": placement() === "below",
					"gvtui-question-context-selector--closed": !isOpen(),
				}}
				style={{ right: `${rightOffset()}px` }}
				selectedTab={selectedTab()}
				onTabSelected={(tab) => selectTab(tab as QuestionContextSelectorTab)}
			>
				<For each={props.tabs}>{(tab) => tabContent[tab]()}</For>
			</Tabs>
		</>
	);
};
